import React from 'react'

import PdfSection from '@/components/misc/ReferralESignatureConsentPdf/components/PdfSection'
import SectionHeading from '@/components/misc/ReferralESignatureConsentPdf/components/SectionHeading'
import { REFERRAL_CONSENT_STAGE } from '@/lib/constants'
import { toSafeHttpUrl } from '@/lib/helpers/helperFunctions'
import { ReferralConsentStage } from '@/types/misc'
import { ReferralWithESignatureDetailsItem } from '@/types/responses/referrals'

interface Props {
  referral: ReferralWithESignatureDetailsItem
  shouldUseGroupLetterVariant: boolean
  shouldIncludeGroupVariantAppendix: boolean
  consentStage: ReferralConsentStage
}

const DataAndPersonalInformationSection: React.FC<Props> = ({
  referral,
  shouldUseGroupLetterVariant,
  shouldIncludeGroupVariantAppendix,
  consentStage,
}) => {
  const { fromCompany, toCompany } = referral
  const fromCompanyName = fromCompany.presentationName
  const toCompanyName = toCompany.presentationName
  const toCompanyGroup = toCompany.group
  const toCompanyGroupName = toCompanyGroup?.name
  const isIntroductionConsent =
    consentStage === REFERRAL_CONSENT_STAGE.INTRODUCTION

  const privacyPolicyUrl = shouldUseGroupLetterVariant
    ? toCompanyGroup?.privacyPolicyUrl
    : toCompany.privacyPolicyUrl

  return (
    <PdfSection className="mb-6">
      <SectionHeading>Your data and personal information</SectionHeading>

      {isIntroductionConsent ? (
        <p>
          To facilitate this introduction, we would like your consent to
          securely share the necessary personal data you have provided to us
          with {toCompanyName}. This will enable them to contact you and provide
          the requested services.
        </p>
      ) : (
        <>
          <p>
            In making the introduction, {fromCompanyName} shared the personal
            information necessary to connect you with {toCompanyName}, including
            your name and contact details.
          </p>

          <p>
            We are asking you to confirm your consent to that information having
            been shared for the purpose of the introduction, and to the
            continued use of that information in connection with the financial
            advice or services you have requested.
          </p>
        </>
      )}

      {shouldUseGroupLetterVariant && (
        <p>
          Where necessary, you also consent to your referral and relevant
          personal information being transferred to another {toCompanyGroupName}{' '}
          firm for the purpose of allocating or continuing to service your
          referral.
          {shouldIncludeGroupVariantAppendix && (
            <>
              {' '}
              Details of the other {toCompanyGroupName} firms are set out at the
              end of this letter.
            </>
          )}
        </p>
      )}

      <p className="mb-2">How your Information will be used:</p>

      <ul>
        <li>
          {fromCompanyName}{' '}
          {isIntroductionConsent ? 'will share' : 'has shared'} only the
          information relevant to the introduction, including, e.g., your name,
          and contact details.
        </li>

        <li>
          {shouldUseGroupLetterVariant
            ? `The relevant ${toCompanyGroupName} firm`
            : toCompanyName}{' '}
          will use this information solely for the purpose of providing the
          services outlined above and in compliance with applicable data
          protection laws.
        </li>

        <li>
          {shouldUseGroupLetterVariant
            ? `The relevant ${toCompanyGroupName} firm`
            : toCompanyName}{' '}
          may share limited information with {fromCompanyName} for
          administrative purposes (e.g., confirming whether an appointment
          occurred).
        </li>

        {privacyPolicyUrl && (
          <li>
            The personal information you submit will be received and processed
            by{' '}
            {shouldUseGroupLetterVariant
              ? `the relevant ${toCompanyGroupName} firm`
              : toCompanyName}{' '}
            as Data Controller, in accordance with their Privacy Notice (link:{' '}
            <a href={toSafeHttpUrl(privacyPolicyUrl) ?? undefined}>
              {privacyPolicyUrl}
            </a>
            ).
          </li>
        )}
      </ul>
    </PdfSection>
  )
}

export default DataAndPersonalInformationSection
